// Hlavička info stránky s fotkou přes celou šířku (#320, KHODER-594).
//
// ZDROJOVÁ PODOBA = hlavička kontaktu (`design_handoff_kontakt/README.md` §1.1,
// prototyp `Kontakt redesign 2a{,- mobil}.dc.html`): full-bleed fotka, tmavý
// přechod zleva (mobil zdola), drobečky přesunuté do fotky, textový sloupec
// 560 px s nativním H1. Kontakt ji staví přes `buildPageHero()` a paritu dál
// hlídá `tools/audit/contact-parity.mjs` (0 rozdílů) — hodnoty v
// `components/_page-hero.scss` jsou proto hodnoty prototypu kontaktu.
//
// FOTKA PER STRÁNKA = redaktorský admin blok HTML kódů (vzor `#bs-cat-photos`,
// `#bs-brand-logos`), klíčem je slug stránky z `body.in-<slug>`:
//   <div id="bs-page-photos" hidden>
//     <span data-bs-page="doprava-platba"
//           data-bs-img="/user/documents/upload/assets/media/doprava-hero-1800.jpg"
//           data-bs-img-mobil="/user/documents/upload/assets/media/doprava-hero-mobil.jpg"
//           data-bs-alt="" data-bs-pos="50% 35%" data-bs-pos-mobil="50% 28%"></span>
//   </div>
// `data-bs-img-mobil` = užší výřez pro ≤ 768 px (`<picture><source media>`),
// `data-bs-pos(-mobil)` = `object-position` výřezu; obojí nepovinné.
// ⛔ Stránka BEZ záznamu zůstává přesně v dnešní podobě — žádný odhad, žádná
// cizí ani výchozí fotka (rozhodnutí Tomáše v #320). Naplnění bloku je datový
// krok (#292), ne kód.
//
// KDE SE HLAVIČKA S FOTKOU KRESLÍ (`initPageHero`, boot po všech info stránkách):
//   • jen na stránkách se stránkovým CSS `page-info.css` (INFO_PAGES
//     v pageCss.js) — jinde by pás zůstal bez stylů;
//   • jen nad jednosloupcovým hero (`.bs-ip__hero` bez `--split`): doprava
//     a platba, servis, hodnocení obchodu, časté dotazy, stav objednávky,
//     reklamace (odstoupení má stat kartu). Celý `.bs-ip__hero` (eyebrow, H1,
//     perex, CTA, poznámka, doplňky stránky) se PŘESOUVÁ do textového sloupce
//     pásu, takže odkazy modulů na něj (`orderStatusPage` čte perex z hera)
//     platí dál a v DOM zůstává jeden H1 v `header[itemprop=headline]`.
//   • ⚠️ split hero (B2B, prodejna s fotkou fasády, odstoupení se stat kartou) má
//     vpravo vlastní vizuál (fotka fasády, karta úrovní) a varianta s fotkou pro
//     ně NEMÁ schválený návrh — záznam v bloku se tam zatím ignoruje.
//   • kontakt si pás staví sám (`contactPage.js`) — záznam v bloku u něj jen
//     vymění fotku týmu.

import { icon } from './icon.js';
import { el, link, textOf } from './infoPage.js';
import { pageCssForBody } from './pageCss.js';

export const PHOTOS_BLOCK = 'bs-page-photos';

// Slug info stránky tak, jak ho Shoptet dává do `body.in-<slug>` (řídí se URL,
// takže přežije i přejmenování stránky v administraci bez úpravy manifestu).
export function currentPageSlug(body) {
  const b = body || document.body;
  const m = b ? String(b.className).match(/(?:^|\s)in-([a-z0-9-]+)(?=\s|$)/) : null;
  return m ? m[1] : null;
}

function attr(node, name) {
  const v = node.getAttribute(name);
  return v && v.trim() ? v.trim() : null;
}

/**
 * Záznam fotky stránky z admin bloku `#bs-page-photos`.
 * @returns {{src:string, srcMobile:string|null, alt:string, pos:string|null, posMobile:string|null}|null}
 *   null = stránka záznam nemá (nebo blok neexistuje) → žádná fotka
 */
export function pagePhoto(slug, doc) {
  const d = doc || document;
  if (!slug) return null;
  const host = d.getElementById(PHOTOS_BLOCK);
  if (!host) return null;
  const def = host.querySelector('[data-bs-page="' + String(slug).replace(/["\\]/g, '') + '"][data-bs-img]');
  const src = def ? attr(def, 'data-bs-img') : null;
  if (!src) return null;
  return {
    src: src,
    srcMobile: attr(def, 'data-bs-img-mobil'),
    alt: def.getAttribute('data-bs-alt') || '',
    pos: attr(def, 'data-bs-pos'),
    posMobile: attr(def, 'data-bs-pos-mobil'),
  };
}

// Nativní drobečky (schema.org BreadcrumbList) se PŘESOUVAJÍ do fotky — kopie
// by rozbila strukturovaná data. Bez nich (jiná šablona) stavíme minimální
// náhradu, ať pás nemá díru.
export function takeCrumbs(title) {
  const native = document.querySelector('#content-wrapper .breadcrumbs, .breadcrumbs');
  if (native) {
    native.classList.add('bs-pghero__crumbs');
    return native;
  }
  const box = el('div', 'breadcrumbs bs-pghero__crumbs');
  const home = link('/', 'navigation-home-icon');
  home.appendChild(icon('home'));
  box.appendChild(home);
  box.appendChild(el('span', 'navigation-bullet', '/'));
  box.appendChild(el('span', null, title || ''));
  return box;
}

// Nativní H1 (schema.org headline) se PŘESOUVÁ i s `header[itemprop=headline]`
// — jinak by v článku zůstal prázdný headline. `text` přepíše nadpis (kontakt
// nese marketingový „Jsme tu pro vás", slovo stránky zůstává v drobečkách).
export function adoptTitle(h1, text) {
  if (!h1) return el('h1', 'bs-pghero__title', text || '');
  h1.classList.add('bs-pghero__title');
  if (text) h1.textContent = text;
  const header = h1.parentElement;
  if (header && header.tagName === 'HEADER') {
    header.classList.add('bs-pghero__title-wrap');
    return header;
  }
  return h1;
}

// Pilulka na fotce: `primary` = červená, `ghost` = průsvitná s bílým rámečkem.
export function heroCta(href, label, iconName, variant) {
  const a = link(href, 'bs-pghero__cta bs-pghero__cta--' + (variant || 'primary'));
  if (iconName) a.appendChild(icon(iconName));
  a.appendChild(el('span', null, label));
  return a;
}

/**
 * Pás s fotkou. `content` = uzly textového sloupce (nadpis, perex, CTA…),
 * `crumbs` = výsledek `takeCrumbs()`.
 * @param {{photo:{src:string, srcMobile?:string|null, alt?:string, pos?:string|null,
 *   posMobile?:string|null, width?:number, height?:number}, crumbs?:Node, content?:Node[]}} opts
 * @returns {HTMLElement} `section.bs-pghero`, textový sloupec je `.bs-pghero__text`
 */
export function buildPageHero(opts) {
  const photo = opts.photo;
  const hero = el('section', 'bs-pghero');
  // výřez per stránka jde přes custom property, ať ho mobilní media query
  // umí přepnout na vlastní (inline `object-position` by vyhrál vždy)
  if (photo.pos) hero.style.setProperty('--bs-pghero-pos', photo.pos);
  if (photo.posMobile) hero.style.setProperty('--bs-pghero-pos-m', photo.posMobile);

  const pic = document.createElement('picture');
  pic.className = 'bs-pghero__pic';
  if (photo.srcMobile) {
    const source = document.createElement('source');
    source.media = '(max-width: 768px)';
    source.srcset = photo.srcMobile;
    pic.appendChild(source);
  }
  const img = document.createElement('img');
  img.className = 'bs-pghero__img';
  img.src = photo.src;
  img.alt = photo.alt || '';
  if (photo.width) img.width = photo.width;
  if (photo.height) img.height = photo.height;
  img.decoding = 'async';
  pic.appendChild(img);
  hero.appendChild(pic);
  hero.appendChild(el('span', 'bs-pghero__scrim'));

  const layer = el('div', 'bs-pghero__layer');
  const inner = el('div', 'bs-pghero__in');
  if (opts.crumbs) inner.appendChild(opts.crumbs);
  const text = el('div', 'bs-pghero__text');
  (opts.content || []).forEach(function (node) {
    if (node) text.appendChild(node);
  });
  inner.appendChild(text);
  layer.appendChild(inner);
  hero.appendChild(layer);
  return hero;
}

// ---------------------------------------------------------------------------
// Info stránky s jednosloupcovým hero — pás s fotkou jen se záznamem v bloku
// ---------------------------------------------------------------------------
export function initPageHero() {
  if (!document.body || document.querySelector('.bs-pghero')) return; // kontakt / idempotence
  if (pageCssForBody(document.body.classList) !== 'info') return;
  const photo = pagePhoto(currentPageSlug());
  if (!photo) return; // bez záznamu stránka zůstává, jak je

  const hero = document.querySelector('.bs-ip > .bs-ip__hero');
  if (!hero || hero.classList.contains('bs-ip__hero--split')) return;

  const band = buildPageHero({ photo: photo, crumbs: takeCrumbs(textOf(hero.querySelector('h1'))) });
  band.classList.add('bs-pghero--page'); // delší obsah než kontakt (odsazení, přechod na mobilu)
  hero.parentNode.insertBefore(band, hero);
  hero.classList.add('bs-ip__hero--photo');
  band.querySelector('.bs-pghero__text').appendChild(hero);
}
